import React from 'react';
import Slider from 'react-slick';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';

import { TemplateClassNames } from '../../../../../../types/template-layouts';
import { Button } from '../../../../../general-ui/button/button';
import Icon, { ICONS, COLORS } from '../../../../../general-ui/icon';
import { TypographyItem } from '../../../../../general-ui/typography-item/typography-item';
import { LanguagesAbbr, Product as ProductType } from '../../../../../../types/working-model';
import { PrevArrow, NextArrow } from '../../../../../general-ui/arrows/arrows';
import { settings } from '../../../../../../utils/slider-utils';
import { useTranslate } from '../../../../../../i18n/useTranslationModules';
import useTranslationRoute from '../../../../../live-event/hooks/use-translation-route';
import { shouldShowBuyNow } from '../../../../../../utils/products';
import { OptionalComponent } from '../../../../../../utils/optional-component';

interface IProductProps {
	id: string;
	product: ProductType;
	editProduct: () => void;
	replaceProduct: () => void;
	removeProduct: () => void;
	template: string;
	language: LanguagesAbbr
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default function Product(props: IProductProps): JSX.Element {
	const { attributes, listeners, setNodeRef, transform } = useSortable({
		id: props.id,
	});

	const namespace = useTranslationRoute();
	const { t } = useTranslate([namespace, 'homepage']);

	const {
		product,
		editProduct,
		replaceProduct,
		removeProduct,
		template,
		language
	} = props;

	const style = {
		transform: CSS.Transform.toString(transform),
		transition: 'none',
	};

	const shouldDisplayBuyNow = shouldShowBuyNow(product.buy_now_link, product.settings?.bypass_modal);

	function getValue(key: keyof ProductType) {
		return t(`${namespace}:product.${product.module_id}.${key}`, product?.[key]?.[language] ?? product?.[key]?.base);
	}

	return (
		<li
			ref={setNodeRef}
			style={style}
			{...attributes}
			{...listeners}
			className="product"
		>
			<div className="product-item-image-container">
				<Slider
					prevArrow={<PrevArrow />}
					nextArrow={<NextArrow />}
					{...settings}
				>
					{product.images.map((image: string, index: number) => (
						<React.Fragment key={index}>
							<img src={image} alt={image} aria-hidden="true" />
						</React.Fragment>
					))}
				</Slider>
				{
					product?.sale_price?.base && (
						<span className="product-item-label">Sale</span>
					)
				}
			</div>
			<div className="product-item-content">
				<TypographyItem
					className={classNames('product-item-title stable', {
						'evt-heading-3': template === TemplateClassNames.Apollo,
						'evt-heading-4': template !== TemplateClassNames.Apollo,
					})}
					tagName="h3"
				>
					{t(`${namespace}:product.${product.module_id}.title`, product?.['product_title']?.[language] as string ?? product?.['product_title']?.base as string)}
				</TypographyItem>
				<div className="price-container">
					<TypographyItem
						className={classNames('product-item-price stable', {
							'evt-heading-5': template !== TemplateClassNames['Studio 54'],
							'evt-caption-1': template === TemplateClassNames['Studio 54'],
						})}
					>
						{
							product?.price?.base ? (
								`$ ${getValue('sale_price') || getValue('price')}`
							) : null
						}
					</TypographyItem>
					{
						(product?.sale_price?.base && product?.price?.base) ? (
							<TypographyItem className={classNames('product-item-old-price evt-body-text-2 stable')}>
								$ {getValue('price')}
							</TypographyItem>
						) : null
					}
				</div>
				<TypographyItem
					className={classNames(
						'evt-body-text-2 product-item-description',
						{ 'increase-description': !product?.price?.base }
					)}
					tagName="p">
					{getValue('description')}
				</TypographyItem>
				<OptionalComponent display={shouldDisplayBuyNow}>
					<Button classButton="primary buy-now-btn" template={template}>
						{getValue('button_text') || t(`homepage:eventAgenda.Learn More`)}
					</Button>
				</OptionalComponent>
			</div>


			<div className="display-controls" >
				<button className="round product-control" onClick={editProduct}>
					<Icon name={ICONS.EDIT} size={12} color={COLORS.DARK_GREY} />
				</button>
				<button className="round product-control" onClick={replaceProduct}>
					<Icon name={ICONS.ARROW_CIRCLE} size={12} color={COLORS.DARK_GREY} />
				</button>
				<button className="round product-control" onClick={removeProduct}>
					<Icon name={ICONS.DELETE} size={12} color={COLORS.DARK_GREY} />
				</button>
			</div>
		</li>
	);
}
